import { useEffect, useState, useRef } from 'react';

import {
	Button,
	DatePicker,
	Flex,
	Form,
	Input,
	Modal,
	Radio,
	Select,
	Typography,
	message,
} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';

import {
	useGetLanguages,
	useSaveQACollectionData,
	useUpdateQACollection,
} from '@/api/batch_process';
import { useOpenSearch } from '@/hooks/useOpenSearch';
import { ParsedQueryResponse } from '@/api/opensearch_results';

import styles from './style.module.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

type TProps = {
	isOpen: boolean;
	onClose?: () => void;
	data: any | null;
	clearData: (data: any) => void;
	onFiltersApplied?: () => void;
	onSearch?: (query: string) => void;
	onHybridSearch?: (params: {
		semantic_query: string;
		keyword_query?: string;
		entity_query?: string;
		date_range?: { from_date: string; to_date: string };
		analysis_date_range?: { from_date: string; to_date: string };
		platform?: string;
		language?: string;
		reach?: string;
		sentiment?: string;
		entity_logic?: string;
		original_question?: string; // Add original question for Q&A
	}) => void;
};

type TDateRange = {
	from: string;
	till: string;
};

const dateFormat = 'D MMM, YYYY';

const PLATFORMS = ['YouTube', 'Instagram', 'TikTok'];
const TIME_PERIODS = [
	{ label: 'Today', days: 0 },
	{ label: 'Last 7 days', days: 7 },
	{ label: '1 month', days: 30 },
	{ label: '3 months', days: 90 },
	{ label: '1 year', days: 365 },
	{ label: 'All time', days: null },
];
const SENTIMENTS = ['Positive', 'Negative', 'Neutral'];
const REACH = ['Mega', 'Macro', 'Micro', 'Nano'];
const AND_OR_OPTIONS = ['and', 'or'];

export default function FiltersSection({ isOpen, data, clearData, onFiltersApplied, onSearch, onHybridSearch }: TProps) {
	const [form] = Form.useForm();
	const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
	
	// Two-step workflow state
	const [parsedQuery, setParsedQuery] = useState<ParsedQueryResponse | null>(null);
	const [isValidating, setIsValidating] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [isApplying, setIsApplying] = useState(false);
	
	// OpenSearch hook
	const { parseQuery } = useOpenSearch();
	
	// Track if user has manually changed date ranges
	const userChangedVideoDateRange = useRef(false);
	const userChangedAnalysisDateRange = useRef(false);

	const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
		data?.filter_data?.platform ? [data.filter_data.platform] : ['*'],
	);
	const [brands, setBrands] = useState<string[]>(
		data ? data?.filter_data?.brands : [],
	);
	const [keywords, setKeywords] = useState<string[]>(
		data ? data?.filter_data?.keywords : [],
	);
	const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
		data
			? data?.filter_data?.languages?.map((lang: any) => lang?.language_name)
			: ['*'],
	);
	const [langs, setLangs] = useState<{ code: string; language_name: string }[]>(
		[],
	);
	const [selectedSentiments, setSelectedSentiments] = useState<string[]>(
		data ? data?.filter_data?.sentiments : ['*'],
	);
	const [selectedReach, setSelectedReach] = useState<string[]>(
		data ? data?.filter_data?.reach : ['*'],
	);
	const [analysisDateRange, setAnalysisDateRange] = useState<TDateRange>(
		data
			? data?.filter_data?.analysisDateRange
			: {
				from: '',
				till: '',
			},
	);
	const [videoDateRange, setVideoDateRange] = useState<TDateRange>(
		data
			? data?.filter_data?.videoDateRange
			: {
				from: '',
				till: '',
			},
	);
	
	// Track which time period buttons are selected (undefined means no button is selected)
	const [selectedAnalysisPeriod, setSelectedAnalysisPeriod] = useState<number | null | undefined>(undefined);
	const [selectedVideoPeriod, setSelectedVideoPeriod] = useState<number | null | undefined>(undefined);
	const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
	
	// Entity logic state (OR/AND for entities)
	const [entityLogic, setEntityLogic] = useState<string>('or');

	const {
		data: languagesData,
		isLoading: isLanguagesLoading,
		isFetching: isLanguagesFetching,
	} = useGetLanguages(isOpen);
	const { mutate, isLoading } = useSaveQACollectionData();
	const { mutate: update, isLoading: isUpdateLoading } =
		useUpdateQACollection();

	const onClear = () => {
		setSelectedPlatforms(['*']);
		setBrands([]);
		setKeywords([]);
		setSelectedSentiments(['*']);
		setSelectedReach(['*']);
		setSelectedLanguages(['*']);
		setAnalysisDateRange({ from: '', till: '' });
		setVideoDateRange({ from: '', till: '' });
		setIsAdvancedOpen(false);
		
		// Clear two-step workflow state
		setParsedQuery(null);
		setValidationError(null);

		form.resetFields();
		clearData(null);
	};

	// Validate query using parse-query endpoint
	const handleValidateQuery = async () => {
		try {
			const values = form.getFieldsValue();
			const question = values.question?.trim();
			
			if (!question) {
				message.error('Please enter a question');
				return;
			}
			
			setIsValidating(true);
			setValidationError(null);
			
			const parsed = await parseQuery(question);
			setParsedQuery(parsed);
			
			// Populate form fields with parsed data
			populateFormFields(parsed);
			
			// Auto-expand advanced block when validation succeeds
			setIsAdvancedOpen(true);
			
			message.success('Query validated successfully!');
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to validate query';
			setValidationError(errorMessage);
			message.error(`Validation failed: ${errorMessage}`);
		} finally {
			setIsValidating(false);
		}
	};

	// Populate form fields with parsed query data
	const populateFormFields = (parsed: ParsedQueryResponse) => {
		// Update the main question field with semantic query
		form.setFieldValue('question', parsed.semantic_query);
		
		// Populate keywords if available
		if (parsed.keyword_query) {
			const keywordList = parsed.keyword_query.split(',').map(k => k.trim()).filter(k => k);
			setKeywords(keywordList);
		}
		
		// Populate entities as brands if available
		if (parsed.entity_query) {
			const entityList = parsed.entity_query.split(',').map(e => e.trim()).filter(e => e);
			setBrands(entityList);
		}
		
		// Set entity logic to 'or' by default (as requested)
		setEntityLogic('or');
		form.setFieldValue('brandsCondition', 'or');
		
		// Populate platform if available
		if (parsed.platform) {
			// Map lowercase platform to capitalized version for UI and handle comma-separated values
			const platformMap: Record<string, string> = {
				'youtube': 'YouTube',
				'instagram': 'Instagram',
				'tiktok': 'TikTok',
			};
			const platformValues = parsed.platform.split(',').map(value => value.trim());
			const platformNames = platformValues.map(value => platformMap[value.toLowerCase()] || value);
			setSelectedPlatforms(platformNames);
		}
		
		// Populate language if available
		if (parsed.language) {
			// Map language codes to language names and handle comma-separated values
			const languageMap: Record<string, string> = {
				'en': 'English',
				'es': 'Spanish', 
				'fr': 'French',
				'de': 'German',
				'it': 'Italian',
				'pt': 'Portuguese',
				'ru': 'Russian',
				'zh': 'Chinese',
				'ja': 'Japanese',
				'ko': 'Korean',
				'ar': 'Arabic',
				'hi': 'Hindi',
			};
			const languageCodes = parsed.language.split(',').map(code => code.trim());
			const languageNames = languageCodes.map(code => languageMap[code] || code);
			setSelectedLanguages(languageNames);
		}
		
		// Populate reach if available
		if (parsed.reach) {
			// Map lowercase reach to capitalized version for UI and handle comma-separated values
			const reachMap: Record<string, string> = {
				'mega': 'Mega',
				'macro': 'Macro',
				'micro': 'Micro',
				'nano': 'Nano',
			};
			const reachValues = parsed.reach.split(',').map(value => value.trim());
			const reachNames = reachValues.map(value => reachMap[value.toLowerCase()] || value);
			setSelectedReach(reachNames);
		}
		
		// Populate sentiment if available
		if (parsed.sentiment) {
			// Map lowercase sentiment to capitalized version for UI and handle comma-separated values
			const sentimentMap: Record<string, string> = {
				'positive': 'Positive',
				'negative': 'Negative',
				'neutral': 'Neutral',
			};
			const sentimentValues = parsed.sentiment.split(',').map(value => value.trim());
			const sentimentNames = sentimentValues.map(value => sentimentMap[value.toLowerCase()] || value);
			setSelectedSentiments(sentimentNames);
		}
		
		// Populate video date range if available
		if (parsed.date_range) {
			const { from_date, to_date } = parsed.date_range;
			if (from_date && to_date) {
				const fromDate = dayjs(from_date);
				const toDate = dayjs(to_date);
				setVideoDateRange({
					from: fromDate.toISOString(),
					till: toDate.toISOString(),
				});
			}
		}
		
		// Populate analysis date range if available, otherwise leave empty
		if (parsed.analysis_date_range) {
			const { from_date, to_date } = parsed.analysis_date_range;
			if (from_date && to_date) {
				const fromDate = dayjs(from_date);
				const toDate = dayjs(to_date);
				setAnalysisDateRange({
					from: fromDate.toISOString(),
					till: toDate.toISOString(),
				});
			}
		}
		// Don't set to "All time" by default - leave empty so no date filter is applied
	};

	// Apply hybrid search with current form data
	const handleApplyFilters = async () => {
		try {
			const values = form.getFieldsValue();
			const question = values.question?.trim();
			
			if (!question) {
				message.error('Please enter a question');
				return;
			}
			
			setIsApplying(true);
			
			// If in simple mode (advanced not open), parse query first then search
			if (!isAdvancedOpen) {
				try {
					// Parse the query first
					const parsed = await parseQuery(question);
					
					// Use parsed data for search parameters
					const searchParams = {
						semantic_query: parsed.semantic_query,
						keyword_query: parsed.keyword_query,
						entity_query: parsed.entity_query,
						date_range: parsed.date_range,
						analysis_date_range: parsed.analysis_date_range,
						platform: parsed.platform,
						language: parsed.language,
						reach: parsed.reach,
						sentiment: parsed.sentiment,
						entity_logic: 'or', // Default to 'or' for simple mode
						original_question: question, // Pass the original question for Q&A
					};
					
					// Call the hybrid search function and wait for it to complete
					if (onHybridSearch) {
						await onHybridSearch(searchParams);
					}
					onFiltersApplied?.();
				} catch (error) {
					message.error('Failed to parse query and search');
					console.error('Parse and search error:', error);
				} finally {
					setIsApplying(false);
				}
				return;
			}
			
			// Advanced mode: use current form data (existing logic)
			const searchParams = {
				semantic_query: question,
				keyword_query: keywords.length > 0 ? keywords.join(', ') : undefined,
				entity_query: brands.length > 0 ? brands.join(', ') : undefined,
				date_range: videoDateRange.from && videoDateRange.till ? {
					from_date: dayjs(videoDateRange.from).format('YYYY-MM-DD'),
					to_date: dayjs(videoDateRange.till).format('YYYY-MM-DD'),
				} : undefined,
				analysis_date_range: analysisDateRange.from && analysisDateRange.till ? {
					from_date: dayjs(analysisDateRange.from).format('YYYY-MM-DD'),
					to_date: dayjs(analysisDateRange.till).format('YYYY-MM-DD'),
				} : undefined,
				platform: selectedPlatforms.length > 0 && selectedPlatforms[0] !== '*' ? 
					selectedPlatforms
						.filter(platform => platform !== '*')
						.map(platform => platform.toLowerCase())
						.join(', ') : undefined,
				language: selectedLanguages.length > 0 && selectedLanguages[0] !== '*' ? 
					// Map language names back to language codes and join with commas
					selectedLanguages
						.filter(lang => lang !== '*')
						.map(lang => {
							const languageNameToCode: Record<string, string> = {
								'English': 'en',
								'Spanish': 'es',
								'French': 'fr',
								'German': 'de',
								'Italian': 'it',
								'Portuguese': 'pt',
								'Russian': 'ru',
								'Chinese': 'zh',
								'Japanese': 'ja',
								'Korean': 'ko',
								'Arabic': 'ar',
								'Hindi': 'hi',
							};
							return languageNameToCode[lang] || lang;
						})
						.join(', ') : undefined,
				reach: selectedReach.length > 0 && selectedReach[0] !== '*' ? 
					selectedReach
						.filter(reach => reach !== '*')
						.map(reach => reach.toLowerCase())
						.join(', ') : undefined,
				sentiment: selectedSentiments.length > 0 && selectedSentiments[0] !== '*' ? 
					selectedSentiments
						.filter(sentiment => sentiment !== '*')
						.map(sentiment => sentiment.toLowerCase())
						.join(', ') : undefined,
				entity_logic: entityLogic,
				original_question: question, // Pass the original question for Q&A
			};
			
			// Call the hybrid search function
			onHybridSearch?.(searchParams);
			onFiltersApplied?.();
		} catch (error) {
			message.error('Failed to apply filters');
		} finally {
			setIsApplying(false);
		}
	};

	// Simple function to check if a time period button is selected
	const isTimePeriodSelected = (days: number | null, selectedPeriod: number | null | undefined) => {
		return selectedPeriod !== undefined && days === selectedPeriod;
	};

	const onQuickSelect = (
		days: number | null,
		setter: (range: TDateRange) => void,
		periodSetter: (period: number | null | undefined) => void,
	) => {
		// Set the selected period
		periodSetter(days);
		
		if (days === null) {
			// All time - set from 2000-01-01 to today
			const today = new Date();
			const allTimeStart = new Date('2000-01-01');
			setter({
				from: allTimeStart.toISOString(),
				till: new Date(today.setUTCHours(23, 59, 58, 0)).toISOString(),
			});
		} else {
			const till = new Date();
			const from = new Date(till);
			from.setDate(till.getDate() - days);
			setter({
				from: from.toISOString(),
				till: till.toISOString(),
			});
		}
	};

	const onSaveClick = async () => {
		const values = form.getFieldsValue();

		// try {
		// 	await form.validateFields();

		// 	const { title, question, keywordsCondition, brandsCondition } = values;

		// 	mutate(
		// 		{
		// 			title: title?.trim(),
		// 			question: question?.trim(),
		// 			platform: selectedPlatforms.length > 0 && selectedPlatforms[0] !== '*' ? 
		// 				selectedPlatforms
		// 					.filter(platform => platform !== '*')
		// 					.map(platform => platform.toLowerCase())
		// 					.join(', ') : '',
		// 			brands,
		// 			keywords,
		// 			brandsCondition,
		// 			keywordsCondition,
		// 			sentiments:
		// 				selectedSentiments?.length === 0 ? ['*'] : selectedSentiments,
		// 			reach: selectedReach?.length === 0 ? ['*'] : selectedReach,
		// 			languages:
		// 				selectedLanguages?.[0] === '*'
		// 					? [{ language_name: 'All', code: '*' }]
		// 					: langs?.filter((l) =>
		// 							selectedLanguages?.includes(l.language_name),
		// 					  ) || [],
		// 			analysisDateRange,
		// 			videoDateRange,
		// 		},
		// 		{
		// 			onSuccess: () => {
		// 				message.success('Saved');
		// 				setIsTitleModalOpen(false);
		// 				onClear();
		// 			},
		// 			onError: (error) => {
		// 				const errorMessage =
		// 					(error as any).response?.data?.message ||
		// 					'Failed to save collection.';
		// 				message.error(`Error saving collection: ${errorMessage}`);
		// 			},
		// 		},
		// 	);
		// } catch (error) {
		// 	message.error('Please provide a valid collection title / question.');
		// }
	};

	const onUpdateClick = async () => {
		const values = form.getFieldsValue();

		// try {
		// 	await form.validateFields();

		// 	const { question, keywordsCondition, brandsCondition, favourite } =
		// 		values;

		// 	update(
		// 		{
		// 			id: data?.id,
		// 			title: data?.title?.trim(),
		// 			question: question?.trim(),
		// 			platform: selectedPlatforms.length > 0 && selectedPlatforms[0] !== '*' ? 
		// 				selectedPlatforms
		// 					.filter(platform => platform !== '*')
		// 					.map(platform => platform.toLowerCase())
		// 					.join(', ') : '',
		// 			brands,
		// 			keywords,
		// 			keywordsCondition,
		// 			brandsCondition,
		// 			sentiments: selectedSentiments,
		// 			reach: selectedReach,
		// 			languages:
		// 				selectedLanguages?.[0] === '*'
		// 					? [{ language_name: 'All', code: '*' }]
		// 					: langs?.filter((l) =>
		// 							selectedLanguages?.includes(l.language_name),
		// 					  ) || [],
		// 			analysisDateRange,
		// 			videoDateRange,
		// 		},
		// 		{
		// 			onSuccess: () => {
		// 				message.success('Updated');
		// 			},
		// 			onError: (error) => {
		// 				const errorMessage =
		// 					(error as any).response?.data?.message ||
		// 					'Failed to save collection.';
		// 				message.error(`Error saving collection: ${errorMessage}`);
		// 			},
		// 		},
		// 	);
		// } catch (error) {
		// 	message.error('Please provide a valid collection title / question.');
		// }
	};

	const onLanguageChange = (value: string[]) => {
		const selected = value || [];

		if (selected?.includes('*') && selected.length === 1) {
			setSelectedLanguages(['*']);
		} else if (
			selected?.includes('*') &&
			selected?.[selected.length - 1] === '*'
		) {
			setSelectedLanguages(['*']);
		} else if (selected?.includes('*') && selected.length > 1) {
			setSelectedLanguages(selected?.filter((l) => l !== '*'));
		} else {
			setSelectedLanguages(
				selected.length > 0 ? selected?.filter((l) => l !== '*') : ['*'],
			);
		}
	};

	const onSentimentChange = (value: string[]) => {
		const selected = value || [];

		if (selected?.includes('*') && selected.length === 1) {
			setSelectedSentiments(['*']);
		} else if (
			selected?.includes('*') &&
			selected?.[selected.length - 1] === '*'
		) {
			setSelectedSentiments(['*']);
		} else if (selected?.includes('*') && selected.length > 1) {
			setSelectedSentiments(selected?.filter((l) => l !== '*'));
		} else if (
			!selected?.includes('*') &&
			selected.length === SENTIMENTS.length
		) {
			setSelectedSentiments(['*']);
		} else {
			setSelectedSentiments(
				selected.length > 0 ? selected?.filter((l) => l !== '*') : ['*'],
			);
		}
	};

	const onReachChange = (value: string[]) => {
		const selected = value || [];

		if (selected?.includes('*') && selected.length === 1) {
			setSelectedReach(['*']);
		} else if (
			selected?.includes('*') &&
			selected?.[selected.length - 1] === '*'
		) {
			setSelectedReach(['*']);
		} else if (selected?.includes('*') && selected.length > 1) {
			setSelectedReach(selected?.filter((l) => l !== '*'));
		} else if (!selected?.includes('*') && selected.length === REACH.length) {
			setSelectedReach(['*']);
		} else {
			setSelectedReach(
				selected.length > 0 ? selected?.filter((l) => l !== '*') : ['*'],
			);
		}
	};

	const onPlatformChange = (value: string[]) => {
		const selected = value || [];

		if (selected?.includes('*') && selected.length === 1) {
			setSelectedPlatforms(['*']);
		} else if (
			selected?.includes('*') &&
			selected?.[selected.length - 1] === '*'
		) {
			setSelectedPlatforms(['*']);
		} else if (selected?.includes('*') && selected.length > 1) {
			setSelectedPlatforms(selected?.filter((l) => l !== '*'));
		} else if (!selected?.includes('*') && selected.length === PLATFORMS.length) {
			setSelectedPlatforms(['*']);
		} else {
			setSelectedPlatforms(
				selected.length > 0 ? selected?.filter((l) => l !== '*') : ['*'],
			);
		}
	};

	useEffect(() => {
		if (data) {
			// Set question field from filter_data to match other fields
			form.setFieldValue('question', data?.filter_data?.semantic_query || data?.question || '');
			form.setFieldValue('title', data?.title || '');
			
			// Update all form fields with the new data
			if (data?.filter_data) {
				const filterData = data.filter_data;
				
				// Update date ranges only if user hasn't manually changed them
				if (filterData.videoDateRange && !userChangedVideoDateRange.current) {
					setVideoDateRange(filterData.videoDateRange);
				}
				if (filterData.analysisDateRange && !userChangedAnalysisDateRange.current) {
					setAnalysisDateRange(filterData.analysisDateRange);
				}
				
				// Update platforms
				if (filterData.platform) {
					setSelectedPlatforms([filterData.platform]);
				}
				
				// Update languages
				if (filterData.language) {
					setSelectedLanguages([filterData.language]);
				}
				
				// Update reach
				if (filterData.reach) {
					setSelectedReach([filterData.reach]);
				}
				
				// Update sentiment
				if (filterData.sentiment) {
					setSelectedSentiments([filterData.sentiment]);
				}
				
				// Update keywords
				if (filterData.keywords && filterData.keywords.length > 0) {
					setKeywords(filterData.keywords);
				}
				
				// Update brands
				if (filterData.brands && filterData.brands.length > 0) {
					setBrands(filterData.brands);
				}
				
				// Update entity logic
				if (filterData.entity_logic) {
					form.setFieldValue('brandsCondition', filterData.entity_logic);
				}
			}
		}
	}, [data]);

	useEffect(() => {
		if (languagesData && !isLanguagesLoading && !isLanguagesFetching) {
			setLangs(
				languagesData?.data?.data?.items
					? languagesData?.data?.data?.items
					: [],
			);
		}
	}, [languagesData, isLanguagesLoading, isLanguagesFetching]);

	if (!isOpen) return null;

	return (
		<Flex
			vertical
			gap={16}
			style={{
				border: '1px solid #d9d9d9',
				borderRadius: 8,
				padding: 16,
				//width: '90%',
				marginRight: '16px',
			}}
		>
			<Form
				form={form}
				layout="vertical"
				name="collectionForm"
				initialValues={{
					keywordsCondition: data?.filter_data?.keywordsCondition || 'and',
					brandsCondition: data?.filter_data?.brandsCondition || 'and',
				}}
			>
				<Flex gap={16}>
					<Flex vertical style={{ width: '100%' }} gap={8}>
						<Typography>
							Ask a question about your collection of videos
						</Typography>
						<FormItem
							name="question"
							label={null}
							rules={[
								{ required: true, message: 'Question is required.' },
								{
									whitespace: true,
									message: 'Question cannot be empty.',
								},
							]}
							style={{ marginBottom: 0 }}
						>
							<TextArea
								rows={3}
								placeholder="Question"
								style={{ resize: 'none' }}
							/>
						</FormItem>

						<div className={`${styles.advancedFields} ${isAdvancedOpen ? styles.advancedFieldsVisible : styles.advancedFieldsHidden}`}>
							{isAdvancedOpen && (
								<>
									<Flex gap={16} align="flex-start">
									<Flex vertical gap={8} style={{ width: '30%' }}>
										<Typography>Platform</Typography>
									<Select
										mode="multiple"
										placeholder="Select platforms"
										style={{ width: '100%' }}
										value={selectedPlatforms}
										onChange={onPlatformChange}
										allowClear
										disabled={!parsedQuery}
									>
										<Select.Option value="*" key="*">
											All
										</Select.Option>
										{PLATFORMS.map((item) => (
											<Select.Option value={item} key={item}>
												{item}
											</Select.Option>
										))}
									</Select>
									</Flex>

									<Flex vertical gap={8} style={{ width: '35%' }}>
										<Typography>Brands/Entities</Typography>
										<Flex vertical style={{ width: '100%' }} gap={4}>
											<Select
												style={{ width: '100%' }}
												mode="tags"
												value={brands}
												onChange={(value) => setBrands(value)}
												placeholder="Add brands/entities"
												disabled={!parsedQuery}
											/>
											<Flex justify="flex-end" className={styles.checkboxWrapper}>
												<FormItem name="brandsCondition" noStyle>
													<Radio.Group
														className={styles.checkboxGroup}
														size="small"
														value={entityLogic}
														onChange={(e) => setEntityLogic(e.target.value)}
														disabled={!parsedQuery}
													>
														{AND_OR_OPTIONS.map((option) => (
															<Radio.Button
																className={styles.checkbox}
																value={option}
																key={option}
															>
																{option}
															</Radio.Button>
														))}
													</Radio.Group>
												</FormItem>
											</Flex>
										</Flex>
									</Flex>

									<Flex vertical gap={8} style={{ width: '35%' }}>
										<Typography>Keywords</Typography>
										<Flex vertical style={{ width: '100%' }} gap={4}>
											<Select
												style={{ width: '100%' }}
												mode="tags"
												value={keywords}
												onChange={(value) => setKeywords(value)}
												placeholder="Add keywords"
												disabled={!parsedQuery}
											/>
											<Flex justify="flex-end" className={styles.checkboxWrapper}>
												<FormItem name="keywordsCondition" noStyle>
													<Radio.Group
														className={styles.checkboxGroup}
														size="small"
														disabled={!parsedQuery}
													>
														{AND_OR_OPTIONS.map((option) => (
															<Radio.Button
																className={styles.checkbox}
																value={option}
																key={option}
															>
																{option}
															</Radio.Button>
														))}
													</Radio.Group>
												</FormItem>
											</Flex>
										</Flex>
									</Flex>
								</Flex>

								<Flex gap={16} style={{ width: '100%', marginTop: '24px' }}>
									<Flex vertical style={{ width: '30%' }} gap={8}>
										<Typography>Language</Typography>
										<Select
											mode="multiple"
											value={selectedLanguages}
											style={{ width: '100%' }}
											onChange={onLanguageChange}
											loading={isLanguagesFetching || isLanguagesLoading}
											disabled={!parsedQuery}
										>
											<Option value="*" key="all">
												All
											</Option>
											{langs.map((lang) => (
												<Option key={lang.language_name} value={lang.language_name}>
													{lang.language_name}
												</Option>
											))}
										</Select>
									</Flex>

									<Flex vertical gap={8} style={{ width: '35%' }}>
										<Typography>Sentiment</Typography>

										<Select
											mode="multiple"
											value={selectedSentiments}
											style={{ width: '100%' }}
											onChange={onSentimentChange}
											disabled={!parsedQuery}
										>
											<Option value="*" key="all">
												All
											</Option>
											{SENTIMENTS.map((s) => (
												<Option key={s} value={s}>
													{s}
												</Option>
											))}
										</Select>
									</Flex>

									<Flex vertical gap={8} style={{ width: '35%' }}>
										<Typography>Reach</Typography>
										<Select
											mode="multiple"
											value={selectedReach}
											style={{ width: '100%' }}
											onChange={onReachChange}
											disabled={!parsedQuery}
										>
											<Option value="*" key="all">
												All
											</Option>
											{REACH.map((s) => (
												<Option key={s} value={s}>
													{s}
												</Option>
											))}
										</Select>
									</Flex>
								</Flex>

								<Flex gap={16} style={{ width: '100%', marginTop: '24px' }}>
									<Flex vertical gap={8} style={{ width: '40%' }}>
										<Typography>Analysis date</Typography>
										<Flex vertical>
											<RangePicker
												value={[
													analysisDateRange.from
														? dayjs(analysisDateRange.from)
														: null,
													analysisDateRange.till
														? dayjs(analysisDateRange.till)
														: null,
												]}
												maxDate={dayjs(new Date())}
												format={dateFormat}
												disabled={!parsedQuery}
												allowEmpty={[true, true]}
												onChange={(_, f) => {
													const start = f?.[0],
														end = f?.[1];
													userChangedAnalysisDateRange.current = true;
													setSelectedAnalysisPeriod(undefined); // Clear selected period when user manually changes date
													setAnalysisDateRange({
														from: start ? new Date(start).toISOString() : '',
														till: end
															? new Date(
																new Date(end).setUTCHours(23, 59, 58, 0),
															).toISOString()
															: '',
													});
												}}
											/>
											<Flex gap={4} wrap="wrap">
												{TIME_PERIODS.map(({ label, days }) => (
													<Button
														key={label}
														type="text"
														size="small"
														style={{
															padding: '2px 4px',
															fontSize: '12px',
															backgroundColor: isTimePeriodSelected(days, selectedAnalysisPeriod) ? '#f5f5f5' : 'transparent',
														}}
														onClick={() => onQuickSelect(days, setAnalysisDateRange, setSelectedAnalysisPeriod)}
													>
														{label}
													</Button>
												))}
											</Flex>
										</Flex>
									</Flex>

									<Flex vertical gap={8} style={{ width: '40%' }}>
										<Typography>Video date</Typography>
										<Flex vertical>
											<RangePicker
												value={[
													videoDateRange.from ? dayjs(videoDateRange.from) : null,
													videoDateRange.till ? dayjs(videoDateRange.till) : null,
												]}
												maxDate={dayjs(new Date())}
												format={dateFormat}
												disabled={!parsedQuery}
												allowEmpty={[true, true]}
												onChange={(_, f) => {
													const start = f?.[0],
														end = f?.[1];
													userChangedVideoDateRange.current = true;
													setSelectedVideoPeriod(undefined); // Clear selected period when user manually changes date
													setVideoDateRange({
														from: start ? new Date(start).toISOString() : '',
														till: end
															? new Date(
																new Date(end).setUTCHours(23, 59, 58, 0),
															).toISOString()
															: '',
													});
												}}
											/>
											<Flex gap={4} wrap="wrap">
												{TIME_PERIODS.map(({ label, days }) => (
													<Button
														key={label}
														type="text"
														size="small"
														style={{
															padding: '2px 4px',
															fontSize: '12px',
															backgroundColor: isTimePeriodSelected(days, selectedVideoPeriod) ? '#f5f5f5' : 'transparent',
														}}
														onClick={() => onQuickSelect(days, setVideoDateRange, setSelectedVideoPeriod)}
													>
														{label}
													</Button>
												))}
											</Flex>
										</Flex>
									</Flex>
								</Flex>
							</>
						)}
					</div>
					</Flex>

				</Flex>

				<Flex
					justify="end"
					style={{ width: '100%', marginTop: '16px' }}
					gap={16}
				>
					<Button
						onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
						style={{ marginRight: 'auto' }}
					>
						{isAdvancedOpen ? 'Hide Advanced' : 'Advanced'}
					</Button>
					<Button
						onClick={async () => {
							try {
								await form.validateFields(['question']);
							} catch (error) {
								message.error('Please provide a valid question.');
								return;
							}
							data?.title ? onUpdateClick() : setIsTitleModalOpen(true);
						}}
						loading={isLoading || isUpdateLoading}
						disabled={
							isLoading || isUpdateLoading || data?.is_user_collection === false
						}
					>
						{data?.title
							? `Save as collection "${data?.title}"`
							: 'Save for later'}
					</Button>
					<Button onClick={onClear}>Clear</Button>
					
					{/* Validate button - Only show in advanced mode */}
					{isAdvancedOpen && (
						<Button 
							type="default"
							style={{ 
								backgroundColor: parsedQuery !== null ? '#d9d9d9' : '#52c41a', 
								borderColor: parsedQuery !== null ? '#d9d9d9' : '#52c41a', 
								color: parsedQuery !== null ? 'rgba(0, 0, 0, 0.25)' : 'white' 
							}}
							loading={isValidating}
							disabled={parsedQuery !== null}
							onClick={handleValidateQuery}
						>
							{isValidating ? 'Validating...' : 'Validate'}
						</Button>
					)}
					
					{/* Apply filters button */}
					<Button 
						type="primary" 
						loading={isApplying}
						disabled={isAdvancedOpen ? (!parsedQuery && !validationError) : isApplying}
						onClick={handleApplyFilters}
					>
						{isApplying ? 'Applying...' : 'Apply filters'}
					</Button>
				</Flex>
				
				{/* Show validation error if any */}
				{validationError && (
					<Typography.Text type="danger" style={{ marginTop: 8, display: 'block' }}>
						Validation Error: {validationError}
					</Typography.Text>
				)}

				<Modal
					open={isTitleModalOpen}
					onCancel={() => setIsTitleModalOpen(false)}
					footer={
						<Flex justify="end" gap={16}>
							<Button
								onClick={() => setIsTitleModalOpen(false)}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button
								type="primary"
								onClick={onSaveClick}
								loading={isLoading}
								disabled={isLoading}
							>
								Save
							</Button>
						</Flex>
					}
					title="Provide a title for your collection"
					width={400}
					centered
				>
					<FormItem
						label="Collection Title"
						rules={[
							{ required: true, message: 'Collection title is required.' },
							{
								whitespace: true,
								message: 'Collection title cannot be empty.',
							},
						]}
						name="title"
						layout="vertical"
					>
						<Input
							// value={collectionTitle}
							// onChange={(e) => setCollectionTitle(e.target.value)}
							placeholder="Collection Title"
							style={{ width: '100%' }}
						/>
					</FormItem>
				</Modal>
			</Form>
		</Flex>
	);
}
